// Centralized theme tokens for all 5 brand identity variants.
// Each theme defines the palette used across the marketing kit.
import React from "react";
import QRCode from "./QRCode";
import SilverLogo from "./SilverLogo";
import { resolveImageUrl } from "../context/SiteContext";

const DOMAIN_URL = "https://letgowelllness.com";
const DOMAIN = "letgowelllness.com";

// The signature "LET GO" + script "Wellness" lockup — used across all marketing.
// `size` is the height of the lockup in CSS inches (or any CSS unit). Scales proportionally.
// LET GO always renders in deep forest green; Wellness always in gold-foil script.
export function LetGoWellnessLockup({ size = "1in", stacked = false }) {
  const fontSize = size;
  const scriptSize = `calc(${size} * 0.42)`;
  const goldGradient =
    "linear-gradient(135deg, #8E6F2D 0%, #C9A24B 20%, #F2D77A 40%, #FFE7A0 55%, #D4A845 75%, #9C7A2E 100%)";

  if (stacked) {
    return (
      <div className="inline-flex flex-col items-center">
        <p
          className="font-serif uppercase"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontWeight: 600,
            fontSize,
            letterSpacing: "0.04em",
            lineHeight: 0.95,
            margin: 0,
            color: "#152A1D",
            textShadow: "0 1px 0 rgba(255,255,255,0.12)",
            whiteSpace: "nowrap",
          }}
        >
          Let Go
        </p>
        <p
          style={{
            fontFamily: "'Sacramento', cursive",
            fontSize: `calc(${size} * 0.6)`,
            letterSpacing: "0.32em",
            lineHeight: 1,
            margin: `calc(${size} * 0.08) 0 0 0`,
            whiteSpace: "nowrap",
            background: goldGradient,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            textAlign: "center",
          }}
        >
          Wellness
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ height: fontSize, minWidth: `calc(${size} * 5.5)` }}
    >
      {/* "LET GO" — deep forest green serif, debossed like the business card, spread wide */}
      <p
        className="font-serif uppercase"
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 600,
          fontSize,
          letterSpacing: "0.45em",
          lineHeight: 1,
          margin: 0,
          color: "#152A1D",
          textShadow: "-1px -1px 0 rgba(0,0,0,0.45), 1px 1px 0 rgba(255,255,255,0.22)",
          whiteSpace: "nowrap",
        }}
      >
        Let Go
      </p>
      {/* "Wellness" — Sacramento gold-foil script, spread out, overlapping center */}
      <p
        style={{
          position: "absolute",
          fontFamily: "'Sacramento', cursive",
          fontSize: `calc(${size} * 0.55)`,
          letterSpacing: "0.7em",
          lineHeight: 1,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
          whiteSpace: "nowrap",
          background: goldGradient,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          filter: "drop-shadow(0 0.5px 0 rgba(0,0,0,0.4))",
        }}
      >
        Wellness
      </p>
    </div>
  );
}

export const THEMES = {
  sage: {
    key: "sage",
    name: "Sage",
    surface: "#FDFBF7",
    accent: "#3A5A40",
    ink: "#1C1917",
    inkSoft: "#57534E",
    rule: "#E2DCD0",
    overline: "#9F5C45",
    logoVariant: null, // natural logo
    onDark: false,
  },
  midnight: {
    key: "midnight",
    name: "Midnight",
    surface: "#0E1B2E",
    accent: "#E5B499", // rose gold
    ink: "#FDFBF7",
    inkSoft: "rgba(253,251,247,0.75)",
    rule: "rgba(229,180,153,0.55)",
    overline: "#E5B499",
    logoVariant: "rosegold",
    onDark: true,
  },
  onyx: {
    key: "onyx",
    name: "Onyx",
    surface: "#0A0908",
    accent: "#E8C669", // gold
    ink: "#FDFBF7",
    inkSoft: "rgba(253,251,247,0.75)",
    rule: "rgba(242,215,122,0.55)",
    overline: "#E8C669",
    logoVariant: "gold",
    onDark: true,
  },
  forest: {
    key: "forest",
    name: "Forest",
    surface: "#2C4C3B",
    accent: "#E8C669",
    ink: "#FDFBF7",
    inkSoft: "rgba(253,251,247,0.8)",
    rule: "rgba(242,215,122,0.5)",
    overline: "#E8C669",
    logoVariant: "gold",
    onDark: true,
  },
  imprint: {
    key: "imprint",
    name: "Imprint",
    surface: "#3A5A40",
    surfaceGradient: "linear-gradient(135deg, #3A5A40 0%, #243B2C 50%, #3A5A40 100%)",
    accent: "#243B2C", // darker sage (deboss)
    ink: "#FDFBF7",
    inkSoft: "rgba(253,251,247,0.8)",
    rule: "rgba(255,255,255,0.18)",
    overline: "#FDFBF7",
    logoVariant: null,
    onDark: true,
    embossed: true,
  },
};

// — Themed Letterhead · 8.5" × 11" portrait —
export function ThemedLetterhead({ theme, brand, logoUrl, phone, email, addr1, addr2 }) {
  const t = theme;
  const bg = t.surfaceGradient || t.surface;
  return (
    <div
      className="relative overflow-hidden"
      style={{ width: "8.5in", height: "11in", background: bg, color: t.ink }}
    >
      {/* Optional brushed surface for imprint */}
      {t.embossed && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 3px)",
            opacity: 0.45,
          }}
        />
      )}

      {/* Header */}
      <header
        className="absolute top-0 left-0 right-0 flex items-center justify-between"
        style={{ padding: "0.7in 0.85in 0.4in" }}
      >
        <LetGoWellnessLockup size="0.55in" goldOnly={t.onDark} />
        <p className="font-serif" style={{ fontSize: "0.14in", color: t.inkSoft }}>
          {DOMAIN}
        </p>
      </header>

      {/* Hairline under header */}
      <div
        className="absolute"
        style={{
          left: "0.85in",
          right: "0.85in",
          top: "1.9in",
          height: "1px",
          background: t.rule,
        }}
      />

      {/* Body placeholder — printable letterhead leaves the middle blank for typing */}
      <div
        className="absolute"
        style={{
          left: "0.85in",
          right: "0.85in",
          top: "2.4in",
          color: t.inkSoft,
        }}
      >
        <p
          className="uppercase"
          style={{ fontSize: "0.09in", letterSpacing: "0.28em", color: t.overline }}
        >
          [ Date ]
        </p>
        <p className="mt-6" style={{ fontSize: "0.14in", color: t.ink }}>
          Dear [ Recipient ],
        </p>
        <p className="mt-3 italic font-serif" style={{ fontSize: "0.12in", color: t.inkSoft, lineHeight: 1.7 }}>
          ( Letterhead body — your message goes here. This is a clean template ready for printing or as a digital
          PDF you can attach to insurance documents, referrals or thank-you notes. )
        </p>
      </div>

      {/* Footer */}
      <footer
        className="absolute bottom-0 left-0 right-0 flex items-center justify-between"
        style={{
          padding: "0.4in 0.85in 0.7in",
          borderTop: `1px solid ${t.rule}`,
          marginLeft: "0.85in",
          marginRight: "0.85in",
        }}
      >
        <div>
          <p style={{ fontSize: "0.11in", color: t.ink, lineHeight: 1.5 }}>{phone}</p>
          <p style={{ fontSize: "0.1in", color: t.inkSoft, lineHeight: 1.5 }}>{email}</p>
        </div>
        <div className="text-center">
          <p style={{ fontSize: "0.1in", color: t.inkSoft, lineHeight: 1.5 }}>{addr1}</p>
          <p style={{ fontSize: "0.1in", color: t.inkSoft, lineHeight: 1.5 }}>{addr2}</p>
        </div>
        <div className="bg-white p-1" style={{ width: "0.75in", height: "0.75in" }}>
          <QRCode value={DOMAIN_URL} style={{ width: "100%", height: "100%", display: "block" }} />
        </div>
      </footer>
    </div>
  );
}

// — Themed Social Post · 5.4" × 5.4" square —
export function ThemedSocialPost({ theme, brand, logoUrl }) {
  const t = theme;
  const bg = t.surfaceGradient || t.surface;
  return (
    <div
      className="relative overflow-hidden flex flex-col items-center justify-center text-center"
      style={{ width: "5.4in", height: "5.4in", background: bg, color: t.ink, padding: "0.5in" }}
    >
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.3in", border: `1px solid ${t.rule}` }}
      />
      {logoUrl && (t.logoVariant ? (
        <SilverLogo src={logoUrl} alt={brand} variant={t.logoVariant} size={{ w: "1in", h: "1in" }} />
      ) : (
        <img
          src={resolveImageUrl(logoUrl)}
          alt={brand}
          crossOrigin="anonymous"
          style={{ width: "1in", height: "1in", objectFit: "contain" }}
        />
      ))}
      <div className="mt-4">
        <LetGoWellnessLockup size="0.7in" goldOnly={t.onDark} />
      </div>
      <h2
        className="font-serif font-light mt-6 max-w-[4.5in]"
        style={{ fontSize: "0.55in", color: t.ink, lineHeight: 0.95, letterSpacing: "-0.01em" }}
      >
        You don&apos;t have to <em className="italic" style={{ color: t.accent }}>hold it</em> anymore.
      </h2>
      <p
        className="font-serif italic mt-5"
        style={{ fontSize: "0.2in", color: t.accent }}
      >
        a quiet space to let go.
      </p>
      <p
        className="uppercase mt-auto"
        style={{ fontSize: "0.1in", letterSpacing: "0.28em", color: t.inkSoft }}
      >
        {DOMAIN}
      </p>
    </div>
  );
}

// — Themed Postcard front · 6" × 4" landscape —
export function ThemedPostcardFront({ theme, brand, logoUrl }) {
  const t = theme;
  const bg = t.surfaceGradient || t.surface;
  return (
    <div
      className="relative overflow-hidden flex items-center justify-center text-center"
      style={{ width: "6in", height: "4in", background: bg, color: t.ink, padding: "0.4in" }}
    >
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.22in", border: `1px solid ${t.rule}` }}
      />
      <div>
        {logoUrl && (t.logoVariant ? (
          <SilverLogo src={logoUrl} alt={brand} variant={t.logoVariant} size={{ w: "0.9in", h: "0.9in" }} className="mx-auto mb-3" />
        ) : (
          <img
            src={resolveImageUrl(logoUrl)}
            alt={brand}
            crossOrigin="anonymous"
            style={{ width: "0.9in", height: "0.9in", objectFit: "contain" }}
            className="mx-auto mb-3"
          />
        ))}
        <div className="flex justify-center">
          <LetGoWellnessLockup size="0.65in" goldOnly={t.onDark} />
        </div>
        <h2
          className="font-serif font-light mt-4"
          style={{ fontSize: "0.5in", color: t.ink, lineHeight: 0.95 }}
        >
          You came back for a <em className="italic" style={{ color: t.accent }}>reason</em>.
        </h2>
        <p
          className="font-serif italic mt-3"
          style={{ fontSize: "0.16in", color: t.accent }}
        >
          Share us with someone who needs it.
        </p>
      </div>
    </div>
  );
}

// — Themed Postcard back · 6" × 4" landscape (address side + QR) —
export function ThemedPostcardBack({ theme, brand, phone, addr1, addr2 }) {
  const t = theme;
  const bg = t.surfaceGradient || t.surface;
  return (
    <div
      className="relative overflow-hidden grid"
      style={{ width: "6in", height: "4in", background: bg, color: t.ink, gridTemplateColumns: "1fr 1px 1fr" }}
    >
      <div className="p-6 flex flex-col">
        <p
          className="uppercase"
          style={{ fontSize: "0.075in", letterSpacing: "0.28em", color: t.overline }}
        >
          From {brand}
        </p>
        <p
          className="font-serif italic mt-3 leading-snug"
          style={{ fontSize: "0.17in", color: t.accent }}
        >
          Thank you for trusting us with someone you love. Drop this card to anyone who could use a quiet, modern place to begin.
        </p>
        <div className="mt-auto pt-4" style={{ borderTop: `1px solid ${t.rule}` }}>
          <div className="flex items-center gap-3">
            <div className="bg-white p-1" style={{ border: `1px solid ${t.rule}` }}>
              <QRCode value={DOMAIN_URL} style={{ width: "0.95in", height: "0.95in", objectFit: "contain", display: "block" }} />
            </div>
            <div>
              <p className="font-serif" style={{ fontSize: "0.16in", color: t.ink }}>{DOMAIN}</p>
              <p style={{ fontSize: "0.1in", color: t.inkSoft }}>{phone}</p>
              <p style={{ fontSize: "0.1in", color: t.inkSoft }}>{addr1}</p>
              <p style={{ fontSize: "0.1in", color: t.inkSoft }}>{addr2}</p>
            </div>
          </div>
        </div>
      </div>
      <div style={{ background: t.rule, width: "1px" }} />
      <div className="p-6 flex flex-col">
        <div className="flex justify-end">
          <div
            className="flex items-center justify-center"
            style={{ width: "0.85in", height: "0.95in", border: `1px solid ${t.inkSoft}` }}
          >
            <span
              className="uppercase text-center leading-tight"
              style={{ fontSize: "0.065in", letterSpacing: "0.22em", color: t.inkSoft }}
            >
              Place<br />Stamp<br />Here
            </span>
          </div>
        </div>
        <div className="mt-auto space-y-3">
          <div style={{ borderBottom: `1px solid ${t.inkSoft}`, opacity: 0.45, height: "0.32in" }} />
          <div style={{ borderBottom: `1px solid ${t.inkSoft}`, opacity: 0.45, height: "0.32in" }} />
          <div style={{ borderBottom: `1px solid ${t.inkSoft}`, opacity: 0.45, height: "0.32in" }} />
          <div style={{ borderBottom: `1px solid ${t.inkSoft}`, opacity: 0.45, height: "0.32in" }} />
        </div>
      </div>
    </div>
  );
}

// — Themed Window Decal · 6" × 6" rounded square —
export function ThemedWindowDecal({ theme, brand, logoUrl }) {
  const t = theme;
  const bg = t.surfaceGradient || t.surface;
  return (
    <div
      className="relative overflow-hidden flex flex-col items-center justify-center text-center"
      style={{ width: "6in", height: "6in", background: bg, color: t.ink, borderRadius: "0.5in" }}
    >
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.35in", border: `1px solid ${t.rule}`, borderRadius: "0.35in" }}
      />
      {logoUrl && (t.logoVariant ? (
        <SilverLogo src={logoUrl} alt={brand} variant={t.logoVariant} size={{ w: "1.6in", h: "1.6in" }} />
      ) : (
        <img
          src={resolveImageUrl(logoUrl)}
          alt={brand}
          crossOrigin="anonymous"
          style={{ width: "1.6in", height: "1.6in", objectFit: "contain" }}
        />
      ))}
      <div className="mt-3">
        <LetGoWellnessLockup size="0.75in" goldOnly={t.onDark} />
      </div>
      <p
        className="font-serif italic mt-1"
        style={{ fontSize: "0.17in", color: t.accent }}
      >
        a quiet space to let go.
      </p>
      <div className="bg-white p-2 mt-3" style={{ border: `1px solid ${t.rule}` }}>
        <QRCode value={DOMAIN_URL} style={{ width: "1.3in", height: "1.3in", objectFit: "contain", display: "block" }} />
      </div>
      <p
        className="uppercase mt-3"
        style={{ fontSize: "0.09in", letterSpacing: "0.32em", color: t.inkSoft }}
      >
        Scan · {DOMAIN}
      </p>
    </div>
  );
}

// — Themed Flyer · 8.5" × 11" portrait —
export function ThemedFlyer({ theme, brand, logoUrl, phone, email, addr1, addr2, services = [] }) {
  const t = theme;
  const bg = t.surfaceGradient || t.surface;
  return (
    <div
      className="relative overflow-hidden"
      style={{ width: "8.5in", height: "11in", background: bg, color: t.ink }}
    >
      {/* Top band */}
      <div
        className="flex items-center justify-between"
        style={{ padding: "0.9in 0.85in 0.4in", borderBottom: `1px solid ${t.rule}` }}
      >
        <LetGoWellnessLockup size="0.95in" goldOnly={t.onDark} />
        <p className="font-serif" style={{ fontSize: "0.16in", color: t.inkSoft }}>{DOMAIN}</p>
      </div>

      {/* Hero */}
      <div style={{ padding: "0.6in 0.85in 0" }}>
        <p
          className="uppercase"
          style={{ fontSize: "0.1in", letterSpacing: "0.28em", color: t.overline }}
        >
          New Patients Welcome · In-person & Telehealth
        </p>
        <h1
          className="font-serif font-light mt-5 tracking-tight"
          style={{ fontSize: "0.85in", color: t.ink, lineHeight: 0.95 }}
        >
          You don&apos;t have to <em className="italic" style={{ color: t.accent }}>hold it</em> anymore.
        </h1>
        <p
          className="mt-4"
          style={{ fontSize: "0.17in", color: t.inkSoft, maxWidth: "6in", lineHeight: 1.55 }}
        >
          A modern mental-health clinic in Waterbury, CT — built for the people who carry too much.
          Anxiety, depression, trauma, burnout, couples & family. We meet you where you are.
        </p>
      </div>

      {/* Services + QR row */}
      <div style={{ padding: "0.6in 0.85in 0", display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.6in" }}>
        <div>
          <p
            className="uppercase mb-4"
            style={{ fontSize: "0.09in", letterSpacing: "0.28em", color: t.overline }}
          >
            What we treat
          </p>
          <ul className="grid grid-cols-2 gap-y-3 gap-x-6">
            {services.slice(0, 6).map((s, i) => (
              <li key={s.id || i} className="flex items-start gap-3 pb-2" style={{ borderBottom: `1px solid ${t.rule}` }}>
                <span style={{ width: "0.08in", height: "0.08in", background: t.accent, marginTop: "0.06in", flexShrink: 0 }} />
                <div>
                  <p className="font-serif" style={{ fontSize: "0.18in", color: t.ink, lineHeight: 1.1 }}>{s.name}</p>
                  <p style={{ fontSize: "0.1in", color: t.inkSoft, lineHeight: 1.3, marginTop: "0.02in" }}>{s.blurb}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-white p-3" style={{ border: `1px solid ${t.rule}` }}>
            <QRCode value={DOMAIN_URL} style={{ width: "1.7in", height: "1.7in", display: "block" }} />
          </div>
          <p
            className="uppercase mt-3 text-center"
            style={{ fontSize: "0.1in", letterSpacing: "0.28em", color: t.ink }}
          >
            Scan to enroll
          </p>
          <p
            className="font-serif italic text-center mt-1"
            style={{ fontSize: "0.16in", color: t.accent }}
          >
            {DOMAIN}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div
        className="absolute bottom-0 left-0 right-0 grid grid-cols-3 items-center"
        style={{ padding: "0.5in 0.85in", borderTop: `1px solid ${t.rule}` }}
      >
        <div>
          <p
            className="uppercase"
            style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: t.overline }}
          >
            Visit
          </p>
          <p className="font-serif mt-1" style={{ fontSize: "0.15in", color: t.ink }}>{addr1}</p>
          <p style={{ fontSize: "0.11in", color: t.inkSoft }}>{addr2}</p>
        </div>
        <div className="text-center">
          <p
            className="uppercase"
            style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: t.overline }}
          >
            Call
          </p>
          <p className="font-serif mt-1" style={{ fontSize: "0.15in", color: t.ink }}>{phone}</p>
          <p style={{ fontSize: "0.11in", color: t.inkSoft }}>{email}</p>
        </div>
        <div className="text-right">
          <p
            className="uppercase"
            style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: t.overline }}
          >
            Hours
          </p>
          <p className="font-serif mt-1" style={{ fontSize: "0.13in", color: t.ink }}>Mon–Fri · 8am — 8pm</p>
          <p style={{ fontSize: "0.11in", color: t.inkSoft }}>Sat · 9am — 4pm</p>
        </div>
      </div>
    </div>
  );
}
